'use client';

import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ENGRAVING_FORM_MODAL_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useToasts, useModalActions } from '@headless-workspace/core-ui';
import {
    getIcon,
    GlowButton,
    GlowDivider,
    GlowImage,
    GlowInput,
    GlowPills,
    GlowTextBody,
    GlowTextCaption,
    GlowTypography,
    GlowModal,
    Pill,
    GlowTag,
} from '@headless-workspace/glow-ds';
import { Component, NoEmptyArray } from '@headless-workspace/typings';
import { getFieldErrorKey, ObjectHelpers, SchemaHelpers } from '@headless-workspace/utils';
import {
    EngraveFormReturnType,
    EngraveFormValue,
    FormUtils,
    getEngraveValidationServerAction,
    ProductFormId,
    useEngraveContext,
} from '../../../../src';

type FieldErrorSchema = {
    type: 'inappropriate';
    terms: string[];
};

type ErrorSchema = {
    line1?: FieldErrorSchema | { type: 'required' };
    line2?: FieldErrorSchema;
    line3?: FieldErrorSchema;
};

const PRODUCT_IMAGE_SIZE = 98;
const FIELD_RESET_ERROR_CHARS = 1;
const TERMS_SEPARATOR = ', ';

export type ProductEngraveModalProps = {
    formValueTemplate: EngraveFormValue;
};

export const ProductEngraveFormModal: Component<ProductEngraveModalProps> = ({ formValueTemplate }) => {
    const { product, fields } = formValueTemplate;

    const t = useTranslations('Modal.engrave');
    const { close } = useModalActions();
    const { notifySuccess, notifyError } = useToasts();
    const { engraveFormValues: contextFormValues, setEngraveFormValues: setContextFormValues } = useEngraveContext();

    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [formErrors, setFormErrors] = useState<ErrorSchema>({});

    const [isInEditMode, setIsInEditMode] = useState(false);

    const getFormValues = useCallback(
        (e: HTMLFormElement) => {
            const formData = new FormData(e);
            return {
                font: formData.get(fields.font.id),
                line1: formData.get(fields.line1.id) || undefined,
                line2: fields.line2 ? formData.get(fields.line2.id) || undefined : undefined,
                line3: fields.line3 ? formData.get(fields.line3.id) || undefined : undefined,
            };
        },
        [fields],
    );

    const pills: Pill[] = useMemo(
        () =>
            fields.font.options.map((option) => {
                if (option.value === 'Manuscript') {
                    return {
                        id: option.value,
                        image: getIcon('Manuscript'),
                    };
                }
                return {
                    id: option.value,
                    label: option.label,
                };
            }),
        [fields],
    );

    const formSchema = useMemo(() => {
        const schemaConstraints: Parameters<typeof FormUtils.generateFormSchema>[1] = {
            [fields.line1.id]: {
                regex: {
                    forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                },
                isRequired: fields.line1.isRequired,
            },
        };

        if (fields.line2) {
            schemaConstraints[fields.line2.id] = {
                regex: {
                    forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                },
                isRequired: fields.line2.isRequired,
            };
        }

        if (fields.line3) {
            schemaConstraints[fields.line3.id] = {
                regex: {
                    forbidden: FormUtils.validationRegex.sqlInjectionPattern,
                },
                isRequired: fields.line3.isRequired,
            };
        }

        return FormUtils.generateFormSchema<EngraveFormReturnType>(Object.values(fields), schemaConstraints);
    }, [fields]);

    const submitForm = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            // ----- Start loading state - reset form errors - prevent default form submission behavior
            setIsValidating(true);
            const formValidationErrors: ErrorSchema = {
                line1: undefined,
                line2: undefined,
                line3: undefined,
            };
            e.preventDefault();

            try {
                // ----- Client side validation : form schema parsing, SQL injections
                const formValues = getFormValues(e.currentTarget);

                const parsedData = formSchema.safeParse(formValues);

                if (!parsedData.success) {
                    const error = parsedData.error;

                    if (SchemaHelpers.hasRequiredError(error, 'line1')) {
                        formValidationErrors.line1 = { type: 'required' };
                        setFormErrors(formValidationErrors);
                    } else {
                        // Unhandled error
                        notifyError(t('error.default'));
                    }
                    return;
                }

                const { data } = parsedData;

                // ----- Server side validation : moderation terms
                const moderationTermsValidationResult = await getEngraveValidationServerAction(data);
                const moderationTermsValidation = unwrapResultSuccess(moderationTermsValidationResult);

                if (!moderationTermsValidation) {
                    notifyError(t('error.default'));
                    return;
                }

                if (moderationTermsValidation.line1.length) {
                    formValidationErrors.line1 = { type: 'inappropriate', terms: moderationTermsValidation.line1 };
                }

                if (moderationTermsValidation.line2.length) {
                    formValidationErrors.line2 = { type: 'inappropriate', terms: moderationTermsValidation.line2 };
                }

                if (moderationTermsValidation.line3.length) {
                    formValidationErrors.line3 = { type: 'inappropriate', terms: moderationTermsValidation.line3 };
                }

                if (formValidationErrors.line1 || formValidationErrors.line2 || formValidationErrors.line3) {
                    setFormErrors(formValidationErrors);
                    return;
                }

                // If the validation succeeds, notify success to the user, register engrave data in context and close the modal.
                notifySuccess(t('label.engraveAdded'));
                setContextFormValues(data);
                setIsInEditMode(true);
                close(ENGRAVING_FORM_MODAL_ID);
            } finally {
                // Always disable isValidating state after validation
                setIsValidating(false);
            }
        },
        [formSchema, getFormValues, notifyError, notifySuccess, setContextFormValues, t],
    );

    const handleClose = useCallback(
        (cleanContext?: boolean) => () => {
            if (cleanContext) {
                setContextFormValues({
                    font: '',
                    line1: '',
                    line2: '',
                    line3: '',
                });
                setIsInEditMode(false);
            }
            close(ENGRAVING_FORM_MODAL_ID, true);
        },
        [close, setContextFormValues],
    );

    const handleFormChange = useCallback(
        (e: ChangeEvent<HTMLFormElement>) => {
            const resetFormErrorsForKey = (key: keyof ErrorSchema) => {
                setFormErrors((prevErrors) => ({
                    ...prevErrors,
                    [key]: undefined,
                }));
            };

            const formValues = getFormValues(e.currentTarget);
            for (const [key, value] of Object.entries(formValues)) {
                // If the error is a required field, it is cleared when the user types in the field
                if (formErrors[key as keyof ErrorSchema]?.type === 'required') {
                    if (value) {
                        resetFormErrorsForKey(key as keyof ErrorSchema);
                        return;
                    }
                }
                // When a user empties a field or starts typing after a selection, the field's errors are cleared
                if (!value || (value as string).length === FIELD_RESET_ERROR_CHARS) {
                    resetFormErrorsForKey(key as keyof ErrorSchema);
                }
            }
        },
        [formErrors, getFormValues],
    );

    const getTranslatedError = (
        key: keyof ErrorSchema,
        options: {
            minLength?: number;
            maxLength?: number;
            field?: string;
        } = {},
    ) => {
        const fieldError = getFieldErrorKey({
            hasError: formErrors && !ObjectHelpers.isEmpty(formErrors),
            type: formErrors[key]?.type,
            options,
        });

        switch (fieldError?.key) {
            case 'inappropriate':
                return t('error.inappropriate', {
                    term: (formErrors[key] as FieldErrorSchema)?.terms?.join(TERMS_SEPARATOR),
                });
            case 'required':
                return t('error.required');
            default:
                return undefined;
        }
    };

    const ProductInformation = () => (
        <section className={'flex flex-col gap-1'}>
            <div className={'flex flex-row gap-1'}>
                {product.image && (
                    <GlowImage image={product.image} height={PRODUCT_IMAGE_SIZE} width={PRODUCT_IMAGE_SIZE} />
                )}
                <div className={'flex-1 flex flex-col gap-0.5'}>
                    <GlowTag label={t('label.free')} color={'secondaryGreen'} size={'medium'} />
                    <GlowTextBody text={t('label.description')} />
                </div>
            </div>
            <div className={'flex flex-col gap-0.25'}>
                <GlowTextBody text={product.brand.name} />
                <GlowTypography fontSize={'title4'} text={product.name} fontWeight={'bold'} />
            </div>
        </section>
    );

    const setPartialEngraveFormValues = (values: Partial<EngraveFormReturnType>) => {
        if (!contextFormValues) return;

        setContextFormValues({
            ...contextFormValues,
            ...values,
        });
    };

    return (
        <GlowModal.Container title={t('label.title')} onClick={handleClose()}>
            <form
                id={ProductFormId.Engrave}
                onSubmit={submitForm}
                onChange={handleFormChange}
                className={'flex flex-1 flex-col'}
                noValidate
            >
                <GlowModal.Content>
                    <ProductInformation />
                    <GlowDivider />
                    <section className={'flex flex-1 flex-col gap-1.5'}>
                        <fieldset className={'flex flex-col gap-1'}>
                            <GlowTextBody
                                TagName={'legend'}
                                fontWeight={'bold'}
                                text={t('label.font')}
                                color={'primary'}
                                className={'pb-1'}
                            />
                            {pills.length > 0 && (
                                <GlowPills
                                    key={fields.font.id}
                                    type={'radio'}
                                    inputName={fields.font.id}
                                    contents={pills as NoEmptyArray<Pill>}
                                    defaultSelected={contextFormValues?.font ?? fields.font.defaultValue}
                                    className={'flex flex-1 flex-row gap-0.5 w-full'}
                                    pillClassName={
                                        'flex flex-1 items-center justify-center before:rounded-0.5 has-[:focus-visible]:rounded-0.5'
                                    }
                                    pillWrapperClassName={
                                        'w-full [&>svg]:h-[21px] gap-0.5 items-center justify-center !rounded-0.5 bg-surface-primary cursor-pointer'
                                    }
                                />
                            )}
                        </fieldset>
                        <fieldset className={'flex flex-col gap-1'}>
                            <GlowTextBody
                                TagName={'legend'}
                                fontWeight={'bold'}
                                text={t('label.message')}
                                color={'primary'}
                                className={'pb-1'}
                            />

                            <GlowInput
                                label={t('label.firstLine')}
                                fieldId={fields.line1.id}
                                required={fields.line1.isRequired}
                                maxLength={fields.line1.maxLength}
                                defaultValue={contextFormValues?.line1 ?? fields.line1.defaultValue}
                                errorText={getTranslatedError('line1', {
                                    field: contextFormValues?.line1,
                                })}
                                type={'text'}
                                id={fields.line1.id}
                                name={fields.line1.id}
                                value={contextFormValues?.line1}
                                onChange={(e) => setPartialEngraveFormValues({ line1: e.target.value })}
                            />

                            {fields.line2 && (
                                <GlowInput
                                    label={t('label.secondLine')}
                                    fieldId={fields.line2.id}
                                    required={fields.line2.isRequired}
                                    maxLength={fields.line2.maxLength}
                                    defaultValue={contextFormValues?.line2 ?? fields.line2.defaultValue}
                                    errorText={getTranslatedError('line2', {
                                        field: contextFormValues?.line2,
                                    })}
                                    type={'text'}
                                    id={fields.line2.id}
                                    name={fields.line2.id}
                                    value={contextFormValues?.line2}
                                    onChange={(e) => setPartialEngraveFormValues({ line2: e.target.value })}
                                />
                            )}
                            {fields.line3 && (
                                <GlowInput
                                    label={t('label.thirdLine')}
                                    fieldId={fields.line3.id}
                                    required={fields.line3.isRequired}
                                    maxLength={fields.line3.maxLength}
                                    defaultValue={contextFormValues?.line3 ?? fields.line3.defaultValue}
                                    errorText={getTranslatedError('line3', {
                                        field: contextFormValues?.line3,
                                    })}
                                    type={'text'}
                                    id={fields.line3.id}
                                    name={fields.line3.id}
                                    value={contextFormValues?.line3}
                                    onChange={(e) => setPartialEngraveFormValues({ line3: e.target.value })}
                                />
                            )}
                            <GlowTextCaption text={t('label.caption')} className={'text-text-tertiary'} />
                        </fieldset>
                    </section>
                </GlowModal.Content>
                <GlowModal.Footer>
                    <GlowButton
                        label={isInEditMode ? t('action.editEngrave') : t('action.addEngrave')}
                        variant={'primary'}
                        formId={ProductFormId.Engrave}
                        isSpinnerActive={isValidating}
                        width={'full'}
                    />
                    <GlowButton
                        label={isInEditMode ? t('action.deleteEngrave') : t('action.cancel')}
                        variant={'secondary'}
                        onClick={handleClose(isInEditMode)}
                        width={'full'}
                    />
                </GlowModal.Footer>
            </form>
        </GlowModal.Container>
    );
};
